import PageTitle from "@/react/components/PageTitle";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import Input from "@/react/components/Input";
import React, { useEffect, useMemo, useState } from "react";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";
import { DialplanCard } from "@/react/features/support-tools/dialplans/ui-components/DialplanCard";
import styled from "styled-components";
import { Button } from "@/react/styled";
import SearchIcon from "@8x8/oxygen-icon/novo/SearchIcon";
import { getCountriesByCode } from "@/react/features/support-tools/dialplans/CountryFlags";
import Modal, {
  MODAL_SIZE,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@8x8/oxygen-modal";
import DialplanForm from "@/react/features/support-tools/dialplans/ui-components/DialplanForm";
import { EMPTY_DIALPLAN } from "@/react/features/support-tools/dialplans/constants";
import { useErrorNotification } from "@/react/notifications";
import { COLORS } from "@/react/theme/constants";
import PaginationControls from "@/react/features/support-tools/dialplans/ui-components/PaginationControls";
import useDialplansApi from "@/react/features/support-tools/dialplans/useDialplansApi";

const ControlsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledInput = styled(Input)`
  max-width: 400px;
`;

/**
 * Anything above 2320px of width will represent 5 cards per row, which is enough
 * for most big monitors
 *
 * Monitors bigger than 3000px wide will enlarge the cards, but these cases are
 * expected to be extremely limited thus not justifying the workload
 *
 * Defining a grid with maximum of 5 columns (6 is used for covering the gaps,
 * therefore for simplicity and fewer computations)
 */
const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(100%, max(400px, 100% / 6)), 1fr)
  );
`;

const NoDataInfoParagraph = styled.i`
  color: ${COLORS.tertiary};
  font-size: 24px;
`;

/**
 * 60 = Least Common Multiplier of numbers from 1 to 5, representing all the
 * possible configurations for columns
 */
const PAGE_SIZE = 60;

const emptyDialplanWithCustomer = (customerId) => ({
  ...EMPTY_DIALPLAN,
  customerId,
});

const DialplansList = ({
  tab,
  dialplans,
  createDialplanApi,
  isFetching,
  isCreating,
  fetchingError,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filteredDialplans, setFilteredDialplans] = useState([]);
  const [page, setPage] = useState(1);
  const [globalExpandState, setGlobalExpandState] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0); // Forces re-sync

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateTriggered, setIsCreateTriggered] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);

  useErrorNotification(fetchingError);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (!debouncedSearchQuery) {
      setFilteredDialplans(dialplans);
    } else {
      const filtered = dialplans.filter(
        (dialplan) =>
          getCountriesByCode(dialplan.countryCode).some((country) =>
            country.name
              .toLowerCase()
              .includes(debouncedSearchQuery.toLowerCase())
          ) ||
          dialplan.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
      );

      setFilteredDialplans(filtered);
    }
  }, [debouncedSearchQuery, dialplans]);

  const expandAll = () => {
    setGlobalExpandState(true);
    setResetTrigger((prev) => 1 - prev);
  };

  const collapseAll = () => {
    setGlobalExpandState(false);
    setResetTrigger((prev) => 1 - prev);
  };

  const { handleCreate: handleCreateDialplan } = useDialplansApi({
    createDialplanApi,
    setIsModalOpenFor: setIsCreateModalOpen,
    setIsActionTriggered: setIsCreateTriggered,
  });

  const getPagedDialplans = useMemo(
    () =>
      (filteredDialplans || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, filteredDialplans]
  );

  const maxPages = Math.ceil((filteredDialplans || []).length / PAGE_SIZE);

  const isLoading = isFetching || isCreating;

  return (
    <>
      {isLoading && <OverlaySpinner />}
      {isCreateModalOpen && (
        <Modal
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          onClose={() => setIsCreateModalOpen(false)}
          portalRef={(e) => setPortalTarget(e)}
          size={MODAL_SIZE.BIG}
        >
          <ModalHeader
            title={"Create Dial Plan"}
            onClose={() => setIsCreateModalOpen(false)}
          />
          <ModalContent>
            <DialplanForm
              initialDialplan={emptyDialplanWithCustomer(customerId)}
              menuPortalTarget={portalTarget}
              isActionTriggered={isCreateTriggered}
              action={handleCreateDialplan}
              dialplans={dialplans}
            />
          </ModalContent>
          <ModalFooter>
            <Button onClick={() => setIsCreateTriggered(true)}>Create</Button>
            <Button
              variant={"secondary"}
              onClick={() => setIsCreateModalOpen(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <PageTitle>Dial Plans</PageTitle>
      <PageContentContainer>
        <ControlsContainer>
          <StyledInput
            placeholder="Search dial plans by name or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            iconLeft={SearchIcon}
          />
          <Button
            variant={"primary"}
            onClick={() => setIsCreateModalOpen(true)}
          >
            + Add Dial Plan
          </Button>
          <Button variant={"tertiary"} onClick={expandAll}>
            Expand all
          </Button>
          <Button variant={"secondary"} onClick={collapseAll}>
            Collapse all
          </Button>
        </ControlsContainer>
        {getPagedDialplans.length > 0 ? (
          <>
            <GridContainer>
              {getPagedDialplans.map((dialplan) => (
                <DialplanCard
                  dialplan={dialplan}
                  globalExpandState={globalExpandState}
                  resetTrigger={resetTrigger}
                  tab={tab}
                  dialplans={dialplans}
                />
              ))}
            </GridContainer>
            <PaginationControls
              maxPages={maxPages}
              page={page}
              setPage={setPage}
            />
          </>
        ) : (
          <NoDataInfoParagraph>No Dial Plans</NoDataInfoParagraph>
        )}
      </PageContentContainer>
    </>
  );
};

export default DialplansList;
